import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import FBoxContainer from '../../../base/FBoxContainer';
import DischargeCriteria from './DischargeCriteria';
import Reasons from './Reasons';
import { FORTA_BLUE } from '../../../../utils/colors';
import { treatmentPlanActions } from '../../../../store';
import { dischargeTransitionActions } from '../../../../store/treatment-plan/dischargeTransition.slice';
import { FProgressBar } from '../../../base';
import { DISCHARGE_REASONS_LABELS } from './constants';
import { TreatmentPlanPages } from '../constants/general';
import { Themes } from '../../../../constants';

import type { IDischargeCriteria, IDischargeTransitionReason } from '../../../../utils/types';

export interface ReasonsTransformed {
  [key: string]: IDischargeTransitionReason; //The key comes from the key field of DISCHARGE_REASONS_LABELS
}

function DischargeTransition() {
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const {
    treatmentPlanId: currentTreatmentPlanId,
    dischargeCriteria,
    reasons,
    dischargeLoading,
    reasonsLoading,
    dischargeErrorCount,
    reasonsErrorCount,
  }: {
    treatmentPlanId: number | null;
    dischargeCriteria: IDischargeCriteria;
    reasons: IDischargeTransitionReason[];
    dischargeLoading: boolean;
    reasonsLoading: boolean;
    dischargeErrorCount: number;
    reasonsErrorCount: number;
  } = useSelector((state: any) => state.dischargeTransitions);
  const [transformedReasons, setTransformedReasons] = useState<ReasonsTransformed>({});
  const [currentDischargeId, setCurrentDischargeId] = useState<number | null>(
    dischargeCriteria?.id ?? null,
  );
  const [reasonsPendingState, setReasonsPendingState] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (Number.isInteger(dischargeErrorCount) && Number.isInteger(reasonsErrorCount)) {
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.DischargeTransition]: dischargeErrorCount + reasonsErrorCount,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dischargeErrorCount, reasonsErrorCount]);

  useEffect(() => {
    if (!treatmentPlan) {
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      dispatch(dischargeTransitionActions.resetState());
      return;
    }
    if (treatmentPlan?.id != currentTreatmentPlanId) {
      dispatch(dischargeTransitionActions.resetState());
    }
    dispatch(
      dischargeTransitionActions.updateState({ key: 'treatmentPlanId', data: treatmentPlan?.id }),
    );
    dispatch(
      dischargeTransitionActions.getDischarge({
        treatmentPlanId: treatmentPlan.id,
        getAccessTokenSilently,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan]);

  useEffect(() => {
    if (!dischargeCriteria || !dischargeCriteria?.id) {
      return;
    }
    setCurrentDischargeId(dischargeCriteria?.id);
  }, [dischargeCriteria]);

  useEffect(() => {
    if (!dischargeCriteria || !dischargeCriteria.id) {
      setTransformedReasons({});
      return;
    }
    if (treatmentPlan?.id) {
      dispatch(
        dischargeTransitionActions.getReasons({
          treatmentPlanId: treatmentPlan.id,
          dischargeId: dischargeCriteria.id,
          getAccessTokenSilently,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDischargeId]);

  useEffect(() => {
    if (!reasons) return;
    const transformedReasons: ReasonsTransformed = {};
    reasons.forEach((obj: IDischargeTransitionReason) => {
      if (obj.label === 'Other') {
        transformedReasons['Other'] = obj;
      }
      const reasonLabel = DISCHARGE_REASONS_LABELS.find((dichargeReasonLabel) => {
        return dichargeReasonLabel.text === obj.label;
      });
      if (reasonLabel?.key) {
        transformedReasons[reasonLabel.key] = obj;
      }
    });
    setTransformedReasons(transformedReasons);
  }, [reasons]);

  const handleSaveDischargeCriteria = (dischargeCriteria: IDischargeCriteria) => {
    if (Object.hasOwn(dischargeCriteria, 'id')) {
      //if id exists in DB and object passes verification, it can either be updated or removed
      dispatch(
        dischargeTransitionActions.updateDischarge({
          treatmentPlanId: treatmentPlan.id,
          dischargeId: dischargeCriteria.id,
          getAccessTokenSilently,
          data: dischargeCriteria,
        }),
      );
    } else {
      //if no id is found and object is verified, create the dischargeCriteria
      dispatch(
        dischargeTransitionActions.createDischarge({
          treatmentPlanId: treatmentPlan.id,
          getAccessTokenSilently,
          data: dischargeCriteria,
        }),
      );
    }
  };

  const clearReasonsPendingState = (Reason: IDischargeTransitionReason) => {
    setReasonsPendingState((state: any) => {
      return { ...state, [Reason.label]: false };
    });
  };

  const fetchCreateReason = async (Reason: IDischargeTransitionReason) => {
    if (Reason.id) return;
    //create reason if it's id does not exists, does not account for the description length
    await dispatch(
      dischargeTransitionActions.createReason({
        treatmentPlanId: treatmentPlan.id,
        dischargeId: dischargeCriteria.id,
        data: Reason,
        getAccessTokenSilently,
      }),
    );
    clearReasonsPendingState(Reason);
  };
  const fetchRemoveReason = async (Reason: IDischargeTransitionReason) => {
    if (!Reason.id) return;
    // if object contains id, remove it on unchecking
    await dispatch(
      dischargeTransitionActions.removeReason({
        treatmentPlanId: treatmentPlan.id,
        dischargeId: dischargeCriteria.id,
        reasonId: Reason.id,
        getAccessTokenSilently,
      }),
    );
    clearReasonsPendingState(Reason);
  };
  const fetchUpdateReason = async (Reason: IDischargeTransitionReason, value: string) => {
    if (!Reason.id || !value || value === '') return;
    //update reason if it's id exists
    await dispatch(
      dischargeTransitionActions.updateReason({
        treatmentPlanId: treatmentPlan.id,
        dischargeId: dischargeCriteria.id,
        reasonId: Reason.id,
        data: { ...Reason, description: value },
        getAccessTokenSilently,
      }),
    );
    clearReasonsPendingState(Reason);
  };

  const handleUpdateCheckField = (Reason: IDischargeTransitionReason, checked: boolean) => {
    setReasonsPendingState((state: any) => {
      return { ...state, [Reason.label]: true };
    });
    if (!Reason.id && checked) {
      fetchCreateReason(Reason);
    }
    if (Reason?.id && !checked) {
      fetchRemoveReason(Reason);
    }
  };

  const handUpdateInputField = (Reason: IDischargeTransitionReason, value: string) => {
    setReasonsPendingState((state: any) => {
      return { ...state, [Reason.label]: true };
    });
    if (Reason?.id) {
      fetchUpdateReason(Reason, value);
    }
    if (!Reason.id && value && value?.length > 2) {
      fetchCreateReason(Reason);
    }
  };

  return (
    <>
      <FBoxContainer
        colorTheme={Themes.Primary}
        title="Transition plan and discharge criteria"
        sx={{ mt: 3 }}
      >
        <DischargeCriteria onUpdateField={handleSaveDischargeCriteria} loading={dischargeLoading} />
        <Divider
          sx={{
            height: 3,
            backgroundColor: FORTA_BLUE.primary,
            mt: 3,
            mb: 2,
            borderRadius: '3px',
          }}
        />
        <Reasons
          reasons={transformedReasons}
          dischargeCriteria={dischargeCriteria}
          onUpdateCheckField={handleUpdateCheckField}
          onUpdateInputField={handUpdateInputField}
          reasonsPendingState={reasonsPendingState}
        />

        {(dischargeLoading || reasonsLoading) && <FProgressBar />}
      </FBoxContainer>
    </>
  );
}

export default DischargeTransition;
